import { Component, Input } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { DomainService, GetSenderInfoResponseInterface, Sender, SendersService } from '@vendasta/email';
import { Observable, ReplaySubject, Subject, combineLatest } from 'rxjs';
import { map, shareReplay, startWith, switchMap, tap } from 'rxjs/operators';

export enum DomainChoice {
  INVALID = '----invalid----',
  USE_SENDER_WHITELABEL_DOMAIN = 'whitelabel',
  USE_PLATFORM_DEFAULT = 'platform_default',
}

export function shouldUsePlatformDefault(choice: DomainChoice): boolean {
  switch (choice) {
    case DomainChoice.USE_PLATFORM_DEFAULT:
      return true;
    case DomainChoice.USE_SENDER_WHITELABEL_DOMAIN:
      return false;
    default:
      console.error(`Unexpected choice "${choice}". Falling back to whitelabel choice.`);
      return false;
  }
}

export enum Validity {
  Valid = 'valid',
  Invalid = 'invalid',
}

@Component({
  selector: 'email-domain-chooser',
  templateUrl: './domain-chooser.component.html',
  styleUrls: ['./domain-chooser.component.scss'],
})
export class DomainChooserComponent {
  form: UntypedFormGroup;
  showAlert = true;

  readonly userDomain$: Observable<string>;
  readonly defaultDomain$: Observable<string>;
  readonly userDomainValidity: Observable<Validity>;
  readonly disableUserDomainSelection$: Observable<boolean>;

  readonly sender$$: Subject<Sender> = new ReplaySubject<Sender>(1);

  @Input()
  set sender(v: Sender) {
    this.sender$$.next(v);
  }

  @Input()
  emailSettingsRouterLink: string;

  constructor(
    private formBuilder: UntypedFormBuilder,
    private domains: DomainService,
    private senders: SendersService,
  ) {
    this.form = this.formBuilder.group({
      sendFromDefault: this.formBuilder.control('default-domain', [Validators.required]),
    });

    this.defaultDomain$ = this.sender$$.pipe(
      switchMap((sender) => this.domains.getDefaultDomain(sender.type)),
      map((response) => response.domain),
      shareReplay({ bufferSize: 1, refCount: true }),
    );

    const userDomain$ = this.sender$$.pipe(
      switchMap((sender) => this.senders.getEmailSendingInfo(sender.type, sender.id)),
    );

    const shouldDisable = (r: GetSenderInfoResponseInterface): boolean => {
      return r.isDefaultForSenderType || !r.isPlatformAuthorized;
    };

    this.userDomainValidity = userDomain$.pipe(
      map((user) => shouldDisable(user)),
      map((d) => (d ? Validity.Invalid : Validity.Valid)),
      shareReplay({ bufferSize: 1, refCount: true }),
    );

    this.disableUserDomainSelection$ = this.userDomainValidity.pipe(
      map((v) => v === Validity.Invalid),
      startWith(true),
      tap((invalid) => {
        const value = invalid ? 'default-domain' : 'partner-domain';
        this.form.setValue({ sendFromDefault: value });
        this.form.markAllAsTouched();
      }),
      shareReplay({ bufferSize: 1, refCount: true }),
    );

    this.userDomain$ = combineLatest([userDomain$, this.userDomainValidity]).pipe(
      map(([u, validity]) => (validity === Validity.Valid ? `(${u.domain})` : '(Not configured)')),
      startWith('(loading...)'),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }

  public getChoice(): DomainChoice {
    const sendFromValue = this.form.get('sendFromDefault').value;
    switch (sendFromValue) {
      case 'partner-domain':
        return DomainChoice.USE_SENDER_WHITELABEL_DOMAIN;
      case 'default-domain':
        return DomainChoice.USE_PLATFORM_DEFAULT;
      default:
        return DomainChoice.INVALID;
    }
  }
}
